<template>
  <section
    class="overflow-hidden rounded-xl border border-slate-200 bg-slate-50 shadow-sm dark:border-slate-600 dark:bg-slate-800"
  >
    <header
      class="flex items-center gap-2 border-b border-slate-200 bg-white px-3 py-2 dark:border-slate-600 dark:bg-slate-700"
    >
      <span
        class="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary-500/10 text-primary-600"
      >
        <FontAwesomeIcon :icon="['fal', 'link']" size="sm" />
      </span>
      <h3
        class="text-sm font-semibold text-gray-900 capitalize-first dark:text-gray-100"
      >
        {{ $t('global.payment.link_documents_title') }}
      </h3>
    </header>
    <div class="p-3">
      <div
        class="grid gap-3"
        :class="{ 'md:grid-cols-2': groups.length > 1 }"
        data-testid="link-documents-groups"
      >
        <div
          v-for="group in groups"
          :key="group.key"
          class="flex flex-col gap-1.5"
          :data-testid="`link-documents-${group.key}`"
        >
          <h4
            class="text-xs font-semibold text-gray-500 first-letter:uppercase dark:text-gray-400"
          >
            {{ $t(`global.payment.link_documents_${group.key}`) }}
          </h4>
          <div
            class="grid gap-2"
            :class="{ 'md:grid-cols-2': groups.length === 1 }"
            :data-testid="`link-documents-list-${group.key}`"
          >
            <label
              v-for="document in group.documents"
              :key="document.id"
              class="flex cursor-pointer gap-2 rounded-md border border-slate-200 bg-white px-2 py-1.5 transition select-none focus-within:ring-1 focus-within:ring-indigo-600 has-[:checked]:border-primary-500 has-[:checked]:bg-primary-500/10 dark:border-slate-600 dark:bg-slate-700"
            >
              <input
                v-model="selectedIds"
                type="checkbox"
                class="mt-0.5 size-4 shrink-0 rounded border-slate-300"
                :name="`link_to_invoice_${document.id}`"
                :value="document.id"
              />
              <span class="flex min-w-0 flex-1 flex-col">
                <span class="flex items-baseline justify-between gap-2">
                  <span
                    class="truncate text-sm font-semibold text-gray-900 dark:text-gray-100"
                  >
                    {{ labelOf(document) }}
                  </span>
                  <span
                    class="shrink-0 text-xs text-gray-500 dark:text-gray-400"
                  >
                    {{ dateOf(document) }}
                  </span>
                </span>
                <span
                  class="flex flex-wrap gap-x-3 text-xs text-gray-500 dark:text-gray-400"
                >
                  <span>
                    {{ $t('attributes.total_tvac') }}
                    <span class="font-medium text-gray-900 dark:text-gray-100">
                      {{ amountOf(document.total_tvac, document) }}
                    </span>
                  </span>
                  <span>
                    {{ $t('attributes.total_paid') }}
                    <span class="font-medium text-gray-900 dark:text-gray-100">
                      {{ amountOf(document.total_paid, document) }}
                    </span>
                  </span>
                </span>
                <span
                  v-if="warningsOf(document).length"
                  class="mt-0.5 flex flex-wrap gap-1"
                >
                  <span
                    v-for="warning in warningsOf(document)"
                    :key="warning"
                    class="rounded-full bg-amber-100 px-2 text-[11px] font-medium text-amber-800 dark:bg-amber-900 dark:text-amber-200"
                  >
                    {{ warning }}
                  </span>
                </span>
              </span>
            </label>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import {
  paymentLinkCandidateLabel,
  type PaymentLinkCandidate
} from '@ui/modal/payment/paymentLinkCandidates'

/**
 * The documents of the conversion chain of the one a payment is recorded on (the ones it comes
 * from on one side, the ones that come from it on the other): the user picks the ones the payment
 * is linked to as well.
 */
export default defineComponent({
  name: 'PaymentLinkDocuments',
  components: { FontAwesomeIcon },
  props: {
    modelValue: {
      type: Array as PropType<string[]>,
      required: true
    },
    before: {
      type: Array as PropType<PaymentLinkCandidate[]>,
      default: () => []
    },
    after: {
      type: Array as PropType<PaymentLinkCandidate[]>,
      default: () => []
    }
  },
  emits: ['update:modelValue'],
  computed: {
    selectedIds: {
      get(): string[] {
        return this.modelValue
      },
      set(ids: string[]) {
        this.$emit('update:modelValue', ids)
      }
    },
    groups(): { key: 'before' | 'after'; documents: PaymentLinkCandidate[] }[] {
      return [
        { key: 'before' as const, documents: this.before },
        { key: 'after' as const, documents: this.after }
      ].filter((group) => group.documents.length > 0)
    }
  },
  methods: {
    labelOf(document: PaymentLinkCandidate): string {
      return paymentLinkCandidateLabel((key) => this.$t(key), document)
    },
    dateOf(document: PaymentLinkCandidate): string {
      return this.$h.formatDate(
        document.emission_date ?? document.created_at,
        'LL'
      )
    },
    amountOf(amount: number | string, document: PaymentLinkCandidate): string {
      return this.$h.formatCurrency(amount, document.currency ?? undefined)
    },
    warningsOf(document: PaymentLinkCandidate): string[] {
      return [
        document.exported && this.$t('global.payment.link_documents_exported'),
        document.payments_sync_enabled &&
          this.$t('global.payment.link_documents_sync')
      ].filter((warning): warning is string => !!warning)
    }
  }
})
</script>
